import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "../components/SpeechInput";

export default function AllLeadsPage() {
  const { http, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");

  const showPaymentInfo = user?.role !== "team_member";

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get("/api/enquiries", { params: { status: "ALL" } });
      setItems(res.data.items || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load all leads.");
    } finally {
      setLoading(false);
    }
  }, [http]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(() => {
    const total = items.length;
    const newCount = items.filter((i) => i.status === "NEW").length;
    const inProgressCount = items.filter((i) => i.status === "IN_PROGRESS").length;
    const completedCount = items.filter((i) => i.status === "COMPLETED").length;
    const closedCount = items.filter((i) => i.status === "CLOSED").length;
    return { total, newCount, inProgressCount, completedCount, closedCount };
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (selectedStatus !== "ALL" && item.status !== selectedStatus) {
        return false;
      }
      if (searchTerm.trim()) {
        const query = searchTerm.trim().toLowerCase();
        const appNo = String(item.applicationNo || "").toLowerCase();
        const custName = String(item.customerName || "").toLowerCase();
        const mobile = String(item.customerMobile || "").toLowerCase();
        const workType = String(item.workTypeName || "").toLowerCase();
        const workDesc = String(item.workDescription || item.workName || "").toLowerCase();
        const vehicle = String(item.vehicleNo || "").toLowerCase();
        const creator = String(item.createdByName || "").toLowerCase();
        const assigned = String(item.assignedToName || "").toLowerCase();

        return (
          appNo.includes(query) ||
          custName.includes(query) ||
          mobile.includes(query) ||
          workType.includes(query) ||
          workDesc.includes(query) ||
          vehicle.includes(query) ||
          creator.includes(query) ||
          assigned.includes(query)
        );
      }
      return true;
    });
  }, [items, selectedStatus, searchTerm]);

  const getStatusBadge = (code) => {
    switch (code) {
      case "NEW":
        return <span className="badge text-bg-info">NEW</span>;
      case "IN_PROGRESS":
        return <span className="badge text-bg-warning">IN PROGRESS</span>;
      case "COMPLETED":
        return <span className="badge text-bg-success">COMPLETED</span>;
      case "CLOSED":
        return <span className="badge text-bg-secondary">CLOSED</span>;
      default:
        return <span className="badge text-bg-dark">{code}</span>;
    }
  };

  return (
    <div className="d-grid gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 className="mb-1">All Leads</h4>
          <p className="text-muted mb-0">
            View, search, and monitor all enquiries and leads across all statuses.
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={loadData}>
          Refresh List
        </button>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <div className="row g-3">
        <div className="col-6 col-md-2">
          <div
            className={`card shadow-sm text-center cursor-pointer ${
              selectedStatus === "ALL" ? "border-primary bg-primary text-white" : ""
            }`}
            onClick={() => setSelectedStatus("ALL")}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body py-2 px-1">
              <div className="small">All Leads</div>
              <div className="fs-4 fw-bold">{stats.total}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div
            className={`card shadow-sm text-center ${
              selectedStatus === "NEW" ? "border-info bg-info text-white" : ""
            }`}
            onClick={() => setSelectedStatus("NEW")}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body py-2 px-1">
              <div className="small">New</div>
              <div className="fs-4 fw-bold">{stats.newCount}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div
            className={`card shadow-sm text-center ${
              selectedStatus === "IN_PROGRESS" ? "border-warning bg-warning text-dark" : ""
            }`}
            onClick={() => setSelectedStatus("IN_PROGRESS")}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body py-2 px-1">
              <div className="small">In Progress</div>
              <div className="fs-4 fw-bold">{stats.inProgressCount}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div
            className={`card shadow-sm text-center ${
              selectedStatus === "COMPLETED" ? "border-success bg-success text-white" : ""
            }`}
            onClick={() => setSelectedStatus("COMPLETED")}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body py-2 px-1">
              <div className="small">Completed</div>
              <div className="fs-4 fw-bold">{stats.completedCount}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div
            className={`card shadow-sm text-center ${
              selectedStatus === "CLOSED" ? "border-secondary bg-secondary text-white" : ""
            }`}
            onClick={() => setSelectedStatus("CLOSED")}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body py-2 px-1">
              <div className="small">Closed</div>
              <div className="fs-4 fw-bold">{stats.closedCount}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-3 align-items-center mb-3">
            <div className="col-12 col-md-6 col-lg-8">
              <SpeechInput
                as="input"
                name="searchTerm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by Application No, Customer Name, Mobile, Work, Vehicle..."
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4 text-md-end">
              <span className="text-muted small">
                Showing {filteredItems.length} of {items.length} leads
              </span>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Application No</th>
                  <th>Customer</th>
                  <th>Mobile</th>
                  <th>Work Details</th>
                  <th>Lead Taken By</th>
                  <th>Assigned To</th>
                  {showPaymentInfo ? <th>Payment</th> : null}
                  <th>Status</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={showPaymentInfo ? 9 : 8} className="text-center py-4 text-muted">
                      Loading leads...
                    </td>
                  </tr>
                ) : null}

                {!loading && filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={showPaymentInfo ? 9 : 8} className="text-center py-4 text-muted">
                      No leads found matching criteria.
                    </td>
                  </tr>
                ) : null}

                {!loading &&
                  filteredItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="fw-bold">{item.applicationNo}</div>
                        <div className="text-muted small">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td>
                        <div className="fw-semibold text-uppercase">{item.customerName}</div>
                      </td>
                      <td>{item.customerMobile}</td>
                      <td>
                        <div className="fw-semibold small">{item.workTypeName || "-"}</div>
                        <div className="text-muted small">
                          {item.workDescription || item.workName || "-"}
                        </div>
                        {item.vehicleNo ? (
                          <div className="badge text-bg-light border text-dark mt-1">
                            🚗 {item.vehicleNo}
                          </div>
                        ) : null}
                        {item.rtoApplicationNo ? (
                          <div className="badge text-bg-info text-dark border mt-1 ms-1">
                            📋 RTO: {item.rtoApplicationNo}
                          </div>
                        ) : null}
                        {item.licenseType ? (
                          <div className="badge text-bg-light border text-dark mt-1 ms-1">
                            🪪 {item.licenseType}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <span className="badge text-bg-light text-primary border">
                          {item.createdByName || "Customer"}
                        </span>
                      </td>
                      <td>
                        <span className="fw-semibold">
                          {item.assignedToName || "Unassigned"}
                        </span>
                      </td>
                      {showPaymentInfo ? (
                        <td>
                          {item.paymentAmount !== null && item.paymentAmount !== undefined
                            ? `₹${Number(item.paymentAmount).toFixed(2)}`
                            : "-"}
                        </td>
                      ) : null}
                      <td>{getStatusBadge(item.status)}</td>
                      <td className="text-end">
                        <Link
                          className="btn btn-outline-primary btn-sm"
                          to={`/admin/enquiries/${item.id}`}
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
